import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { useApi } from "../../context/api.js";
import MDBox from "../MDBox";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward.js";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MDButton from "../MDButton";

function GenericQuizExecutor({ activityEndpoint, params, onEnd, children }) {
  const api = useApi();
  const [questions, setQuestions] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [times, setTimes] = useState([]);
  const [valid, setValid] = useState(0);
  const [validAnswer, setValidAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);

  useEffect(() => {
    api.startActivity(activityEndpoint, params).then((questions) => {
      if (questions && questions.length) {
        setQuestions(questions);
        setTimes(times.concat([{ start: Date.now() }]));
      } else {
        onEnd();
      }
    });
  }, []);

  const onAnswerChange = useCallback(
    (e) => {
      if (valid <= 0) {
        setAnswers((answers) => {
          const newAnswers = answers.slice();
          newAnswers[currentQuestion] = e.target.value;

          return newAnswers;
        });
      }
    },
    [currentQuestion, valid]
  );

  const validate = useCallback(() => {
    if (!answers[currentQuestion] && answers[currentQuestion] !== 0) {
      setValid(-1);
    } else {
      // Save end time
      setTimes((times) => {
        const newTimes = times.slice();
        newTimes[currentQuestion] = { ...times[currentQuestion], end: Date.now() };

        return newTimes;
      });

      // Validate
      api
        .validateQuestion(activityEndpoint, {
          id: questions[currentQuestion].id,
          answer: answers[currentQuestion],
        })
        .then((validation) => {
          setTotalScore((s) => s + 1);
          if (validation.valid) {
            setValid(1);
            setScore((s) => s + 1);
          } else {
            setValid(2);
            if (validation.answer || validation.answer === 0) {
              setValidAnswer(validation.answer);
            }
          }
        });
    }
  }, [currentQuestion, answers]);

  const next = useCallback(() => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTimes((times) => {
        const newTimes = times.slice();
        newTimes[currentQuestion + 1] = { start: Date.now() };

        return newTimes;
      });
      setValid(0);
      setValidAnswer(null);
    } else {
      // Validate end of quiz
      api
        .endActivity(activityEndpoint, {
          answers: answers.map((answer, i) => ({
            id: questions[i].id,
            answer,
            ...times[i],
          })),
          milestoneId: params.milestoneId,
        })
        .then((res) => {
          // TODO : Manage error (!res.success)
          onEnd(res["activityId"]);
        });
    }
  }, [currentQuestion, questions && questions.length, times]);

  const submit = useCallback(() => {
    if (valid > 0) {
      next();
    } else {
      validate();
    }
  }, [validate, valid]);

  return (
    <Card
      sx={{
        minWidth: "50%",
        minHeight: "50%",
        maxWidth: "90%",
        maxHeight: "90%",
        p: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        //overflow: "auto",
      }}
    >
      {!questions && <CircularProgress color="info" size={48} />}
      {!!questions && (
        <>
          <MDBox width="100%" display="flex" alignItems="center">
            <span style={{ flex: 1 }}>
              Question {currentQuestion + 1} / {questions.length}
            </span>
            {totalScore > 0 && (
              <span>
                Score : {score} / {totalScore}
              </span>
            )}
          </MDBox>
          <MDBox
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            {children({
              question: questions[currentQuestion],
              answer: answers[currentQuestion],
              onAnswerChange,
              valid,
              validAnswer,
              submit,
            })}
          </MDBox>
          <MDBox width="100%" display="flex" justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color={valid === 1 ? "success" : valid === 2 ? "error" : "info"}
              type="submit"
              disabled={valid <= 0 && !answers[currentQuestion] && answers[currentQuestion] !== 0}
              onClick={valid > 0 ? next : validate}
            >
              {valid > 0
                ? currentQuestion === questions.length - 1
                  ? "Terminer"
                  : "Suivant"
                : "Valider"}
              {valid > 0 ? (
                <ArrowForwardIcon color={"inherit"} sx={{ ml: 1 }} />
              ) : (
                <CheckCircleIcon color={"inherit"} sx={{ ml: 1 }} />
              )}
            </MDButton>
          </MDBox>
        </>
      )}
    </Card>
  );
}
GenericQuizExecutor.propTypes = {
  activityEndpoint: PropTypes.string.isRequired,
  params: PropTypes.object,
  onEnd: PropTypes.func,
  children: PropTypes.func.isRequired,
};

export default GenericQuizExecutor;
